import React from "react";
import { Table, Header, TableProps } from "@amzn/awsui-components-console";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import {
  ARRAY_CELL_WRAPPER,
  BADGE_CELL_WRAPPER,
  getFixedFeeDealDateStatus,
  getFixedFeeDealStatus,
  getFixedFeeType,
} from "../allFixedFeeConfigsPage/fixed-fee-config-attributes";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
}

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

// Helper function to format numeric values
const formatNumber = (value: any) => {
  if (!value) return "-";
  return Number(value.string).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
};

export const TopLevelTable: React.FC<Props> = ({ fixedFeeConfig }) => {
  const topLevelColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    DUMMY_COLUMN_DEFINITION,
    {
      id: "fixedFeeId",
      header: "Fixed Fee ID",
      cell: (item: FixedFeeConfig) => item.fixedFeeId || "-",
    },
    {
      id: "environment",
      header: "Environment",
      cell: (item: FixedFeeConfig) => item.environment || "-",
    },
    {
      id: "dealStatus",
      header: "Deal Status",
      cell: (item: FixedFeeConfig) =>
        BADGE_CELL_WRAPPER(getFixedFeeDealStatus(item)),
      minWidth: 260,
    },
    {
      id: "dealDateStatus",
      header: "Deal Date Status",
      cell: (item: FixedFeeConfig) =>
        BADGE_CELL_WRAPPER(getFixedFeeDealDateStatus(item)),
    },
    {
      id: "fixedFeeType",
      header: "FixedFee Type",
      cell: (item: FixedFeeConfig) => BADGE_CELL_WRAPPER(getFixedFeeType(item)),
    },
    {
      id: "dealStartDate",
      header: "Deal Start Date",
      cell: (item: FixedFeeConfig) => item.dealStartDate || "-",
      minWidth: 120,
    },
    {
      id: "dealEndDate",
      header: "Deal End Date",
      cell: (item: FixedFeeConfig) => item.dealEndDate || "-",
      minWidth: 120,
    },
    {
      id: "effectiveTerminationDate",
      header: "Effective Termination Date",
      cell: (item: FixedFeeConfig) => item.effectiveTerminationDate || "-",
      minWidth: 120,
    },
    {
      id: "resellerIds",
      header: "Reseller IDs",
      cell: (item: FixedFeeConfig) =>
        item.dealConfig?.resellerIds
          ? ARRAY_CELL_WRAPPER(item.dealConfig?.resellerIds)
          : "-",
      maxWidth: 200,
    },
    {
      id: "fusePlanIds",
      header: "Fuse Plan IDs",
      cell: (item: FixedFeeConfig) =>
        item.dealConfig?.fusePlanIds
          ? ARRAY_CELL_WRAPPER(item.dealConfig?.fusePlanIds)
          : "-",
      minWidth: 380,
      maxWidth: 400,
    },
    {
      id: "serviceProvider",
      header: "Service Provider",
      cell: (item: FixedFeeConfig) => item.serviceProvider || "-",
    },
    {
      id: "benefitPeriodDuration",
      header: "Benefit Period Duration",
      cell: (item: FixedFeeConfig) => item.benefitPeriodDuration || "-",
    },
    {
      id: "contractPrice",
      header: "Contract Price",
      cell: (item: FixedFeeConfig) => formatNumber(item.contractPrice),
    },
    {
      id: "initialPlanPrice",
      header: "Initial Plan Price",
      cell: (item: FixedFeeConfig) => formatNumber(item.initialPlanPrice),
    },
    {
      id: "currencyCode",
      header: "Currency Code",
      cell: (item: FixedFeeConfig) => item.contractCurrencyCode || "-",
    },
    {
      id: "adjustmentFrequency",
      header: "Adjustment Frequency",
      cell: (item: FixedFeeConfig) => item.adjustmentFrequency || "-",
    },
    {
      id: "invoiceFrequency",
      header: "Invoice Frequency",
      cell: (item: FixedFeeConfig) => item.invoiceFrequency || "-",
    },
    {
      id: "marketplaceId",
      header: "Marketplace ID",
      cell: (item: FixedFeeConfig) => item.dealConfig?.marketplaceId || "-",
    },
    {
      id: "accountingRegistrationId",
      header: "Accounting Registration ID",
      cell: (item: FixedFeeConfig) => item.accountingRegistrationId || "-",
    },
    DUMMY_COLUMN_DEFINITION,
  ];

  const topLevelData = [fixedFeeConfig];

  return (
    <Table
      columnDefinitions={topLevelColumnDefinitions}
      items={topLevelData}
      wrapLines={true}
      resizableColumns={false}
      header={<Header>Top-Level Information</Header>}
    />
  );
};
